import { gql } from "lib/apolloClient";

import {
  curriculumProgramFragment,
  staffFragment,
  studentAttendanceV2Fragment,
  studentsAlertFragment,
} from "modules/CommonFragments";
import {
  studentNameFragment,
  familyMemberNameFragment,
} from "AttendanceDashboard/modules/AttendanceDashboardFragment";

import { excusalFragment } from "./HomeroomFragments";

export const getExcusalCountQuery = gql`
  query getExcusalCount(
    $id: ID!
    $newExcusalsFilters: AttendanceExcusalsFilter
    $upcomingAndOngoingExcusalsFilters: AttendanceExcusalsFilter
    $markedExcusalsFilters: AttendanceExcusalsFilter
    $studentsFilters: OrgConnectionFilterInput
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        students(filters: $studentsFilters) {
          edges {
            node {
              id
              firstName
              middleName
              lastName
              preferredName
              suffix
              pronouns
              type
              profileImage
              studentAttendanceExcusals {
                totalCount
              }
              alertTypes {
                ...alertTypeFields
              }
            }
          }
        }
        newExcusals: attendanceExcusals(filters: $newExcusalsFilters) {
          totalCount
        }
        upcomingAndOngoingExcusals: attendanceExcusals(
          filters: $upcomingAndOngoingExcusalsFilters
        ) {
          totalCount
        }
        markedExcusals: attendanceExcusals(filters: $markedExcusalsFilters) {
          totalCount
        }
      }
    }
  }
  ${studentsAlertFragment}
`;

export const getStudentAllExcusalQuery = gql`
  query getStudentAllExcusal(
    $id: ID!
    $studentExcusalFilters: AttendanceExcusalFilters
  ) {
    node(id: $id, type: STUDENT) {
      id
      ... on Student {
        id
        studentAttendanceExcusals(filters: $studentExcusalFilters) {
          totalCount
          edges {
            node {
              id
              startDate
              endDate
              reason
              durationType
              periods {
                id
                label
                abbreviation
              }
              createdBy {
                id
                firstName
                lastName
                type
              }
              createdAt
              conversation {
                id
              }
              attendanceOption {
                id
                label
                color
              }
              student {
                id
                firstName
                middleName
                lastName
                profileImage
                courses {
                  id
                  title
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getHomeroomTeacherResponsibleV2Query = gql`
  query getHomeroomTeacherResponsibleV2(
    $id: ID!
    $periodsFilter: PeriodFilterOptions
    $isToddleV2TimetableEnabled: Boolean!
    $isAdminTimeTableEnabled: Boolean!
  ) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        courses {
          id
          periodsV3(filters: $periodsFilter)
            @include(if: $isAdminTimeTableEnabled) {
            edges {
              node {
                period {
                  id
                  label
                }
                day
                rotationDay {
                  id
                  label
                }
                teachers {
                  id
                  firstName
                  lastName
                  middleName
                }
                isSelected
                course {
                  id
                  title
                }
              }
            }
          }
          periodsV4(filters: $periodsFilter)
            @include(if: $isToddleV2TimetableEnabled) {
            edges {
              node {
                period {
                  id
                  label
                }
                day
                rotationEntity {
                  id
                  label
                }
                teachers {
                  staff {
                    id
                    firstName
                    lastName
                    middleName
                  }
                }
                isSelected
                course {
                  id
                  title
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getHomeroomStaffStudentsQuery = gql`
  query getHomeroomStaffStudents(
    $id: ID!
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $studentRelationFilter: StaffStudentRelationFilter
    $attendanceExcusalFilters: AttendanceExcusalFilters
    $attendanceV2Filters: StudentAttendanceFilters
    $enrolledPeriodsFilters: PeriodFilterOptions
    $isAdminTimeTableEnabled: Boolean!
  ) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        studentsRelation(
          filters: $studentRelationFilter
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          edges {
            node {
              id
              ... on Student {
                id
                ...studentNameFields
                profileImage
                email
                isDevUser
                isArchived
                enrollmentDate
                familyMembers {
                  id
                  ...familyMemberNameFields
                  profileImage
                  email
                }
                studentAttendanceExcusals(filters: $attendanceExcusalFilters) {
                  totalCount
                  edges {
                    node {
                      id
                      reason
                    }
                  }
                }
                attendanceV2(filters: $attendanceV2Filters) {
                  edges {
                    node {
                      ...studentAttendanceV2Item
                      isHomeroomAttendance
                    }
                  }
                }
                enrolledPeriods(filters: $enrolledPeriodsFilters)
                  @skip(if: $isAdminTimeTableEnabled) {
                  id
                  isSelected
                  course {
                    id
                  }
                }
                enrolledPeriodsV2(filters: $enrolledPeriodsFilters)
                  @include(if: $isAdminTimeTableEnabled) {
                  isSelected
                  course {
                    id
                  }
                }
                enrolledPeriodsV3(filters: $enrolledPeriodsFilters) {
                  isSelected
                  course {
                    id
                  }
                }
                alertTypes {
                  ...alertTypeFields
                }
              }
            }
          }
        }
      }
    }
  }
  ${studentNameFragment.studentNameFields}
  ${familyMemberNameFragment.familyMemberNameFields}
  ${studentAttendanceV2Fragment.basicAttendanceV2Details}
  ${studentsAlertFragment}
`;

export const getStudentsWithoutHomeroomQuery = gql`
  query getStudentsWithoutHomeroom(
    $id: ID!
    $courseFilters: CourseFilter
    $studentsFilters: OrgConnectionFilterInput
    $first: Int
    $after: String
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $staffRelationFilters: StaffStudentRelationFilter
    $isStudentEdgesRequired: Boolean!
    $studentCurriculumProgramFilter: StudentCurriculumProgramFilter
    $isAllCoursesRequired: Boolean! = false
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        students(
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $studentsFilters
        ) {
          totalCount
          pageInfo @include(if: $isStudentEdgesRequired) {
            hasNextPage
            endCursor
          }
          edges @include(if: $isStudentEdgesRequired) {
            node {
              id
              email
              firstName
              middleName
              lastName
              preferredName
              suffix
              pronouns
              type
              profileImage
              sourceId
              curriculumPrograms(filters: $studentCurriculumProgramFilter) {
                id
              }
              allCourses: courses(filters: $courseFilters)
                @include(if: $isAllCoursesRequired) {
                id
                title
              }
              coursesV2(filters: $courseFilters) {
                totalCount
                archivedCount
              }
              staffRelation(filters: $staffRelationFilters) {
                totalCount
                edges {
                  node {
                    ... on Staff {
                      id
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export const getOrganizationAllCoursesQuery = gql`
  query getOrganizationAllCourses($id: ID!, $filters: CourseFilterInput) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        courses(filters: $filters) {
          edges {
            node {
              id
              title
              curriculumProgram {
                id
                type
              }
            }
          }
        }
      }
    }
  }
`;

export const getHomeroomStudentsCountQuery = gql`
  query getHomeroomStudentsCount(
    $id: ID!
    $staffStudentsFilters: StaffStudentRelationFilter
  ) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        studentsRelation(filters: $staffStudentsFilters) {
          totalCount
          countByCurriculumProgram {
            curriculumProgram {
              id
            }
            count
          }
        }
      }
    }
  }
`;

export const getHomeroomStudentsQuery = gql`
  query getHomeroomStudentsCount(
    $id: ID!
    $staffStudentsFilters: StaffStudentRelationFilter
  ) {
    node(id: $id, type: STAFF) {
      id
      ... on Staff {
        id
        studentsRelation(filters: $staffStudentsFilters) {
          totalCount
          edges {
            node {
              id
              email
              type
              firstName
              middleName
              lastName
              profileImage
              ... on Student {
                preferredName
                prefix
                suffix
                pronouns
              }
            }
          }
        }
      }
    }
  }
`;

export const getCourseMarkedExcusalsQuery = gql`
  query getCourseMarkedExcusalsQuery(
    $id: ID!
    $first: Int
    $after: String
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $activeExcusalsFilters: AttendanceExcusalsFilter
    $upcomingExcusalsFilters: AttendanceExcusalsFilter
    $studentArchivedFilters: UserArchivedFilters
  ) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        activeExcusals: attendanceExcusals(filters: $activeExcusalsFilters) {
          totalCount
          edges {
            cursor
            node {
              ...excusalItem
            }
          }
        }
        upcomingExcusals: attendanceExcusals(
          first: $first
          after: $after
          orderBy: $orderBy
          orderByDirection: $orderByDirection
          filters: $upcomingExcusalsFilters
        ) {
          totalCount
          edges {
            cursor
            node {
              ...excusalItem
            }
          }
          pageInfo {
            hasNextPage
            endCursor
          }
        }
      }
    }
  }
  ${excusalFragment.excusalDetails}
`;

export const getStudentsCountQuery = gql`
  query getStudentsCount($id: ID!, $studentsFilters: OrgConnectionFilterInput) {
    node(id: $id, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        students(filters: $studentsFilters) {
          totalCount
        }
      }
    }
  }
`;

export const getHomeroomStudentFeedQuery = gql`
  query getHomeroomStudentFeed(
    $organizationId: ID!
    $after: String
    $filters: OrgConnectionFilterInput
    $orderBy: ORDER_BY_ENUM
    $orderByDirection: ORDER_BY_DIRECTION
    $staffFilter: StaffStudentRelationFilter
    $withStaffRelation: Boolean! = false
    $userCourseMapFilters: UserCourseMapFilters
    $courseGradeFilters: CourseGradeFilters
    $userYearGroupMapFilters: YearGroupFilters
    $yearGroupGradeFilters: YearGroupGradeFilters
    $userArchivedFilters: UserArchivedFilters
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      id
      ... on Organization {
        id
        students(
          first: 20
          after: $after
          filters: $filters
          orderBy: $orderBy
          orderByDirection: $orderByDirection
        ) {
          edges {
            node {
              id
              firstName
              middleName
              lastName
              pronouns
              preferredName
              prefix
              suffix
              profileImage
              isArchivedV2(filters: $userArchivedFilters)
              isBlocked
              alertTypes {
                ...alertTypeFields
              }
              courseMap(filters: $userCourseMapFilters) {
                course {
                  id
                  title
                  profileImageData {
                    url
                    icon
                  }
                  isArchived
                  curriculumProgram {
                    ...curriculumProgramBasicDetailsItem
                  }
                  genericTags {
                    id
                    label
                    type
                  }
                  grades(filters: $courseGradeFilters) {
                    id
                    name
                  }
                  grade {
                    id
                    yearGroup {
                      id
                    }
                  }
                }
                tags {
                  id
                  label
                }
              }
              yearGroup(filters: $userYearGroupMapFilters) {
                id
                name
                grade(filters: $yearGroupGradeFilters) {
                  id
                  name
                }
                curriculumProgram {
                  ...curriculumProgramBasicDetailsItem
                }
              }
              staffRelation(filters: $staffFilter)
                @include(if: $withStaffRelation) {
                totalCount
                edges {
                  node {
                    ...staffItem
                    ... on Staff {
                      isArchivedV2(filters: $userArchivedFilters)
                    }
                  }
                }
              }
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${curriculumProgramFragment.curriculumProgramBasicDetails}
  ${staffFragment.basicStaffDetails}
  ${studentsAlertFragment}
`;

export const getStaffListQuery = gql`
  query getStaffList(
    $organizationId: ID!
    $searchText: String
    $grades: [ID!]
    $courses: [ID!]
    $archivalState: ARCHIVAL_STATE_ENUM
    $curriculumProgramIds: [ID!]
    $academicYearIds: [ID!]
  ) {
    node(id: $organizationId, type: ORGANIZATION) {
      ... on Organization {
        id
        staff(
          filters: {
            searchText: $searchText
            grades: $grades
            courses: $courses
            archivalState: $archivalState
            curriculumProgramIds: $curriculumProgramIds
            academicYearIds: $academicYearIds
          }
        ) {
          totalCount
          edges {
            node {
              ...userIdentityBasicDetailItem
              curriculumPrograms {
                id
              }
            }
          }
        }
      }
    }
  }
  ${staffFragment.userIdentityBasicDetail}
`;
